import { useQuery } from "@tanstack/react-query";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { useContext } from "react";
import { ListingProperty } from "@/modules/listing-dashboard/dtos/index-v2";
import { getTracks } from "@/modules/property-tracks/api";
import { Track } from "@/modules/property-tracks/types";

const useGetLeadTracks = ({
  enabled = true,
  page,
  pageSize,
}: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
} = {}) => {
  const { lead } = useContext(LeadPageContext);

  const journeyUId = lead?.data?.lead?.journey_uid;

  const {
    data: leadListingsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["getLeadTracks", journeyUId, page, pageSize],
    queryFn: () =>
      getTracks({
        filters: {
          journeyUId: journeyUId,
        },
        pagination: {
          page: page ?? 1,
          pageSize: pageSize ?? 20,
        },
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!journeyUId && enabled,
  });

  const leadListings: ListingProperty[] =
    leadListingsData?.data?.results?.map((track: Track): ListingProperty => {
      // Extract price info
      const activelistingPrice = track?.property.propertyListingPrice;
      const activeClientAskingPrice = track?.property.propertyPrice;

      // Extract owner from customerProperty
      const owner = track?.property.customerProperty?.find(
        (cp) => cp.currentOwner
      )?.customer;
      return {
        // ========== MAPPABLE FIELDS ==========

        // Full property object with all fields
        property: {
          uid: track.property.uid,
          unitId: track.property.unitId,
          description: track.property.description,
          unitArea: track.property.unitArea,
          phase: track.property.phase,
          numberOfBedrooms: track.property.numberOfBedrooms,
          numberOfBathrooms: track.property.numberOfBathrooms,
          deliveryDate: track.property.deliveryDate,
          readyToMove: track.property.readyToMove,
          floorNumber: track.property.floorNumber,
          numberOfFloors: track.property.numberOfFloors,
          gardenArea: track.property.gardenArea,
          roofArea: track.property.roofArea,
          landArea: track.property.landArea,
          finishing: track.property.finishing,
          lastInstallmentDate: track.property.lastInstallmentDate,
          websiteListingId: track.property.websiteListingId,
          compound: track.property.compound,
          propertyType: {
            uid: "",
            name: track.property.propertyType?.name ?? "",
          },
          // propertyPrice array (convert from single price object if needed)
          propertyPrice: activeClientAskingPrice,
          // propertyTrack array
          // propertyTrack: track.property.propertyTrack || [],
          // customerProperty array
          customerProperty: track.property.customerProperty,
          isUnderConstruction: track.property.isUnderConstruction,
          // Price fields (legacy naming)
          actualPrice: activelistingPrice?.price,
          askingPrice: activeClientAskingPrice?.price,
          downpayment: activelistingPrice?.downPayment,
          remainingInstallments: activelistingPrice?.remainingInstallments,
          maintenance: activelistingPrice?.maintenance,

          // Bedroom/bathroom (legacy naming)
          bedrooms: track.property.numberOfBedrooms,
          bathrooms: track.property.numberOfBathrooms,

          // Owner info
          ownerName: owner?.name,
          // ownerId: owner?.uid,

          // Delivery/ready info (legacy naming)
          readyBy: track.property.deliveryDate,

          // Unit identifiers
          unitNumber: track.property.unitId,

          listingAgentEmail: track.user?.email,

          status: track.status.name,

          propertyTrack: [
            {
              uid: track.uid,
              trackStage: track.trackStage,
              trackType: track.trackType,
              journey: track.journey,
              user: track.user,
            },
          ],

          unitAddress: `${track?.property.compound?.name || ""} ${
            track?.property.compound?.area?.name || ""
          }`,

          updatedAt: track.updatedAt.toString(),
        },
        // Track-related info
        // trackStage: track.trackStage,
        // trackType: track.trackType,
        // journey: track.journey,
        // user: track.user,

        // Compound info
        // compound: track.property.compound,

        // Unit address (computed)
        // unitAddress: `${track?.property.compound?.name || ""} ${
        //   track?.property.compound?.area?.name || ""
        // }`.trim(),

        // Status (track status object)
        // status: track.status,

        // ========== UNMAPPABLE FIELDS (NOT AVAILABLE IN TRACK) ==========
        // NOTE: These fields cannot be mapped as Track/Property doesn't contain them:
        // - id: number (Track uses uid instead)
        // - inventoryListingId: number (not in Track)
        // - listingType: ListingType (not in Track - need to determine from business logic)
        // - active: boolean (not in Track - assume true or get from listing context)
        // - lastPublishedAt: Date (not in Track - track doesn't have listing publication info)
        // - trackDocuments: TrackDocument[] (not directly in Track.property, might be elsewhere)
        // - propertyRental: PropertyRental (not directly accessible)
        // - isRented: boolean (not in Track)
        // - ListingStatus object: {uid, name, color, etc.} (Track only has Status with limited fields)
        // - deletedAt: string | null (not in Track)
        // - oldId: number | string | null (not in Track)
        // - migrationOrgId: number | string | null (not in Track)
        // - listingAgentEmail: string (not in Track - could use track.user?.email if needed)
        // - lead: complex object (not in Track)
        // - images: [] (commented out - would need track.trackDocuments)
        // - ownersListingId: number (not in Track)
      };
    }) || [];

  const count = leadListingsData?.data?.total || 0;

  return { leadListings, isLoading, isError, count };
};

export default useGetLeadTracks;
